import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Building2, TrendingUp, TrendingDown, Minus, DollarSign, Users, Activity, BarChart3, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';

interface AgencyAnalytics {
  agencyId: string;
  agencyName: string;
  agencyCode: string;
  totalSessions: number;
  homeCases: number;
  pcpCases: number;
  urgentCareCases: number;
  erCases: number;
  diversionRate: number;
  estimatedSavings: number;
}

// Default hospital costs per recommendation level
const DEFAULT_COSTS = {
  HOME: 150,
  PCP: 450,
  URGENT_CARE: 500,
  ER_NOW: 2500,
};

// HIPAA COMPLIANCE NOTE: This component is ONLY accessible to super admins
// Agency admins cannot access this component - see AdminAnalyticsPage.tsx
// The AgencyComparisonTable shows cross-agency data which is restricted to super admins only

type RecommendationType = 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW';

interface AgencyComparisonTableProps {
  startDate?: Date;
  endDate?: Date;
  selectedRecommendations?: RecommendationType[];
}

export default function AgencyComparisonTable({ startDate, endDate, selectedRecommendations = ['HOME', 'PCP', 'URGENT_CARE', 'ER_NOW'] }: AgencyComparisonTableProps) {
  const chartColors = useChartColors();
  const [data, setData] = useState<AgencyAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortColumn, setSortColumn] = useState<keyof AgencyAnalytics>('estimatedSavings');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [chartMetric, setChartMetric] = useState<'savings' | 'diversion'>('savings');

  useEffect(() => {
    const loadAllAgencyAnalytics = async () => {
      setLoading(true);
      try {
        // Get all approved agencies
        const { data: agencies, error: agenciesError } = await supabase
          .from('agencies')
          .select('id, name, code, hospital_visit_cost')
          .eq('is_approved', true)
          .order('name');

        if (agenciesError) throw agenciesError;
        if (!agencies || agencies.length === 0) {
          setData([]);
          return;
        }

        // Get analytics for each agency
        const analyticsPromises = agencies.map(async (agency) => {
          const { data: analytics, error } = await supabase
            .rpc('get_cost_savings_analytics', {
              _agency_id: agency.id,
              _start_date: startDate ? startDate.toISOString() : null,
              _end_date: endDate ? (() => {
                const endOfDay = new Date(endDate);
                endOfDay.setHours(23, 59, 59, 999);
                return endOfDay.toISOString();
              })() : null
            });

          if (error) {
            console.error(`Error getting analytics for ${agency.name}:`, error);
            return null;
          }

          const typedAnalytics = analytics as {
            totalSessions: number;
            homeCases: number;
            pcpCases: number;
            urgentCareCases: number;
            erCases: number;
          };

          const hospitalCost = (agency.hospital_visit_cost as number) || DEFAULT_COSTS.ER_NOW;
          
          // Apply recommendation filter
          const homeCases = selectedRecommendations.includes('HOME') ? typedAnalytics.homeCases : 0;
          const pcpCases = selectedRecommendations.includes('PCP') ? typedAnalytics.pcpCases : 0;
          const urgentCareCases = selectedRecommendations.includes('URGENT_CARE') ? typedAnalytics.urgentCareCases : 0;
          const erCases = selectedRecommendations.includes('ER_NOW') ? typedAnalytics.erCases : 0;
          
          // Calculate filtered total
          const filteredTotal = homeCases + pcpCases + urgentCareCases + erCases;
          
          // Calculate diverted cases (HOME + PCP + URGENT_CARE)
          const divertedCases = homeCases + pcpCases + urgentCareCases;
          const diversionRate = filteredTotal > 0 
            ? (divertedCases / filteredTotal) * 100 
            : 0;

          // Calculate savings
          const estimatedSavings = 
            (homeCases * (hospitalCost - DEFAULT_COSTS.HOME)) +
            (pcpCases * (hospitalCost - DEFAULT_COSTS.PCP)) +
            (urgentCareCases * (hospitalCost - DEFAULT_COSTS.URGENT_CARE));

          return {
            agencyId: agency.id,
            agencyName: agency.name,
            agencyCode: agency.code,
            totalSessions: filteredTotal,
            homeCases,
            pcpCases,
            urgentCareCases,
            erCases,
            diversionRate,
            estimatedSavings,
          };
        });

        const results = await Promise.all(analyticsPromises);
        setData(results.filter((r): r is AgencyAnalytics => r !== null));
      } catch (error) {
        console.error('Error loading agency comparison data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllAgencyAnalytics();
  }, [startDate, endDate, selectedRecommendations]);

  const handleSort = (column: keyof AgencyAnalytics) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortColumn];
    const bVal = b[sortColumn];
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }
    
    return sortDirection === 'asc' 
      ? (aVal as number) - (bVal as number) 
      : (bVal as number) - (aVal as number);
  });

  // Calculate averages for comparison
  const avgDiversionRate = data.length > 0 
    ? data.reduce((sum, d) => sum + d.diversionRate, 0) / data.length 
    : 0;

  const getDiversionBadge = (rate: number) => {
    if (rate >= avgDiversionRate + 10) {
      return <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1"><TrendingUp className="h-3 w-3" /> High</Badge>;
    }
    if (rate <= avgDiversionRate - 10) {
      return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 gap-1"><TrendingDown className="h-3 w-3" /> Low</Badge>;
    }
    return <Badge className="bg-muted text-muted-foreground gap-1"><Minus className="h-3 w-3" /> Avg</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const SortableHeader = ({ column, children }: { column: keyof AgencyAnalytics; children: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortColumn === column && (
          <span className="text-xs">{sortDirection === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </TableHead>
  );

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Agency Comparison
          </CardTitle>
          <CardDescription>Loading agency data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Agency Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No agency data available for comparison.
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data - sorted by selected metric
  const chartData = [...data]
    .sort((a, b) => 
      chartMetric === 'savings' 
        ? b.estimatedSavings - a.estimatedSavings 
        : b.diversionRate - a.diversionRate
    )
    .slice(0, 10) // Top 10
    .map((agency) => ({
      name: agency.agencyCode,
      fullName: agency.agencyName,
      value: chartMetric === 'savings' ? agency.estimatedSavings : agency.diversionRate,
    }));

  // Color gradient for bars
  const getBarColor = (index: number, total: number) => {
    const hue = chartMetric === 'savings' ? 160 : 220; // Green for savings, Blue for diversion
    const lightness = 45 + (index / total) * 20;
    return `hsl(${hue}, 70%, ${lightness}%)`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-foreground">{data.fullName}</p>
          <p className="text-sm text-muted-foreground">
            {chartMetric === 'savings' 
              ? formatCurrency(data.value)
              : `${data.value.toFixed(1)}% diversion rate`
            }
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          Agency Comparison
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p className="text-sm">Uses the date range selected in the filters above. Shows comparison across all agencies.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <CardDescription>
          Side-by-side analytics comparison across all agencies. Click column headers to sort.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Chart Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Top 10 Agencies by</span>
            </div>
            <Tabs value={chartMetric} onValueChange={(v) => setChartMetric(v as 'savings' | 'diversion')}>
              <TabsList className="h-8">
                <TabsTrigger value="savings" className="text-xs gap-1 px-3">
                  <DollarSign className="h-3 w-3" />
                  Savings
                </TabsTrigger>
                <TabsTrigger value="diversion" className="text-xs gap-1 px-3">
                  <Users className="h-3 w-3" />
                  Diversion Rate
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 20, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="hsl(var(--border))" />
                <XAxis 
                  type="number" 
                  tickFormatter={(value) => 
                    chartMetric === 'savings' 
                      ? `$${(value / 1000).toFixed(0)}k` 
                      : `${value}%`
                  }
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={70}
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(index, chartData.length)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Table Section */}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <SortableHeader column="agencyName">Agency</SortableHeader>
                <SortableHeader column="totalSessions">
                  <Activity className="h-4 w-4 mr-1" />Sessions
                </SortableHeader>
                <SortableHeader column="homeCases">Home</SortableHeader>
                <SortableHeader column="pcpCases">PCP</SortableHeader>
                <SortableHeader column="urgentCareCases">Urgent</SortableHeader>
                <SortableHeader column="erCases">ER</SortableHeader>
                <SortableHeader column="diversionRate">
                  <Users className="h-4 w-4 mr-1" />Diversion %
                </SortableHeader>
                <SortableHeader column="estimatedSavings">
                  <DollarSign className="h-4 w-4 mr-1" />Savings
                </SortableHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((agency, index) => (
                <TableRow 
                  key={agency.agencyId}
                  className={cn(index % 2 === 0 ? 'bg-background' : 'bg-muted/20')}
                >
                  <TableCell className="font-medium">
                    <div>
                      <div>{agency.agencyName}</div>
                      <div className="text-xs text-muted-foreground">{agency.agencyCode}</div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{agency.totalSessions.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-emerald-600">{agency.homeCases.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-blue-600">{agency.pcpCases.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-amber-600">{agency.urgentCareCases.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-red-600">{agency.erCases.toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono">{agency.diversionRate.toFixed(1)}%</span>
                      {getDiversionBadge(agency.diversionRate)}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono font-semibold text-emerald-600">
                    {formatCurrency(agency.estimatedSavings)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Summary Row */}
        <div className="mt-4 p-4 bg-muted/30 rounded-lg border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-muted-foreground">Total Agencies</div>
              <div className="text-lg font-semibold">{data.length}</div>
            </div>
            <div>
              <div className="text-muted-foreground">Total Sessions</div>
              <div className="text-lg font-semibold">
                {data.reduce((sum, d) => sum + d.totalSessions, 0).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground">Avg Diversion Rate</div>
              <div className="text-lg font-semibold">{avgDiversionRate.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-muted-foreground">Total Savings</div>
              <div className="text-lg font-semibold text-emerald-600">
                {formatCurrency(data.reduce((sum, d) => sum + d.estimatedSavings, 0))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
